/**
 * Editor toolbar presets for the mail surfaces (#624, plan §13.2 N6).
 *
 * `RichTextEditor` owns one registry of commands; a preset picks which of them
 * a surface gets, and in what order. The keys are `RichTextEditor`'s
 * `TOOLBAR_KEYS`; `'|'` renders a divider, and dividers are collapsed after
 * gating so an emptied group never leaves a stray rule on the bar.
 */

/**
 * The mail toolbar — replies, forwards and new mail.
 *
 * What it OMITS is the point. Align L/C/R, Horizontal Rule, Remove Link,
 * Content Library and Insert Button are campaign and sequence authoring tools:
 * they belong to a designed email that someone lays out, not to a reply typed
 * into a thread. Shipping them on a reply box is how the editor ended up asking
 * a user to consider centre-aligning a two-line answer.
 *
 * 'attach' is named here but is NOT one of the editor's controls — attachments
 * are `AttachmentBar`'s, rendered by `MailComposer` directly below the editor.
 * `RichTextEditor` skips keys it has no entry for, so the preset can describe
 * the surface's whole toolbar in one readable line-up while each half renders
 * from wherever it actually lives.
 *
 * 'ai' still passes through `showAIWriter`, which stays a hard gate: naming a
 * key here asks for a control, it does not grant one.
 */
export const MAIL_TOOLBAR = [
    'undo', 'redo', '|',
    'bold', 'italic', 'underline', 'strike', 'code', '|',
    'bulletList', 'orderedList', 'blockquote', '|',
    'link', 'image', 'attach', '|',
    'ai',
];

/** Named so a test can assert the omission is deliberate, not an oversight. */
export const MAIL_TOOLBAR_EXCLUDES = [
    'alignLeft', 'alignCenter', 'alignRight',
    'horizontalRule',
    'unlink',
    'contentLibrary',
    'emailButton',
];

/**
 * The full-page compose toolbar — `ComposePage` and `PersonalComposePage`.
 *
 * MAIL_TOOLBAR is written for a REPLY. Those two surfaces are not replies: they
 * are full-page new mail, and they deliberately pass `showMergeVariables` and
 * `showContentLibrary`. Applying the reply preset to them takes the merge
 * variable dropdown and the Content Library button off a page that asked for
 * both — a silent removal, not a design decision.
 *
 * So the wider preset exists, and either surface opts in with one line:
 *
 *     <MailComposer toolbar={MAIL_COMPOSE_TOOLBAR} ... />
 *
 * It still omits Align L/C/R, Horizontal Rule, Remove Link and Insert Button —
 * those are campaign/sequence layout tools wherever they appear.
 */
export const MAIL_COMPOSE_TOOLBAR = [
    'undo', 'redo', '|',
    'bold', 'italic', 'underline', 'strike', 'code', '|',
    'bulletList', 'orderedList', 'blockquote', '|',
    'link', 'image', 'contentLibrary', 'attach', '|',
    'mergeVariables', 'ai',
];
